/**
 * DateRangeFilter Component - T113
 *
 * Sprint 006 A4: Zeitraum-Chips erweitert (Quartale + letztes Jahr, aktiver
 * Chip wird markiert) und auf die kanonische FilterPill umgestellt.
 */
import { FilterPill, Input } from '../ui';

interface DateRangeFilterProps {
  dateFrom: string | undefined;
  dateTo: string | undefined;
  onChange: (dateFrom: string | undefined, dateTo: string | undefined) => void;
}

function iso(d: Date): string {
  // Lokales Datum, nicht UTC — toISOString würde westlich von UTC den Vortag liefern.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface Preset {
  label: string;
  range: () => [string, string];
}

const PRESETS: Preset[] = [
  {
    label: 'Dieses Quartal',
    range: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      return [iso(new Date(now.getFullYear(), q * 3, 1)), iso(new Date(now.getFullYear(), q * 3 + 3, 0))];
    },
  },
  {
    label: 'Letztes Quartal',
    range: () => {
      const now = new Date();
      let q = Math.floor(now.getMonth() / 3) - 1;
      let year = now.getFullYear();
      if (q < 0) {
        q = 3;
        year -= 1;
      }
      return [iso(new Date(year, q * 3, 1)), iso(new Date(year, q * 3 + 3, 0))];
    },
  },
  {
    label: 'Dieses Jahr',
    range: () => {
      const y = new Date().getFullYear();
      return [iso(new Date(y, 0, 1)), iso(new Date(y, 11, 31))];
    },
  },
  {
    label: 'Letztes Jahr',
    range: () => {
      const y = new Date().getFullYear() - 1;
      return [iso(new Date(y, 0, 1)), iso(new Date(y, 11, 31))];
    },
  },
  {
    label: '30 Tage',
    range: () => {
      const now = new Date();
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return [iso(from), iso(now)];
    },
  },
];

export function DateRangeFilter({ dateFrom, dateTo, onChange }: DateRangeFilterProps) {
  const hasRange = dateFrom || dateTo;

  const isActive = (preset: Preset) => {
    const [from, to] = preset.range();
    return dateFrom === from && dateTo === to;
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => (
          <FilterPill
            key={preset.label}
            active={isActive(preset)}
            onClick={() => {
              const [from, to] = preset.range();
              onChange(from, to);
            }}
          >
            {preset.label}
          </FilterPill>
        ))}
        {hasRange && (
          <button
            onClick={() => onChange(undefined, undefined)}
            className="px-3 py-1.5 rounded-lg text-sm text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            Zurücksetzen
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="block text-xs text-white/40 mb-1">Von</label>
          <Input
            type="date"
            value={dateFrom ?? ''}
            onChange={(e) => onChange(e.target.value || undefined, dateTo)}
            className="text-sm"
          />
        </div>
        <span className="text-white/30 mt-5">–</span>
        <div className="flex-1">
          <label className="block text-xs text-white/40 mb-1">Bis</label>
          <Input
            type="date"
            value={dateTo ?? ''}
            onChange={(e) => onChange(dateFrom, e.target.value || undefined)}
            className="text-sm"
          />
        </div>
      </div>
    </div>
  );
}
